<template>
  <Tag class="connection-pill" severity="secondary" rounded :value="label">
    <template #icon>
      <span class="connection-pill__dot" :class="`connection-pill__dot--${state}`" />
    </template>
  </Tag>
</template>

<script setup>
import { computed } from 'vue';
import Tag from 'primevue/tag';

// Stream health in the top bar.
//
// Deliberately a NEUTRAL pill with a colored dot rather than a color-filled
// tag: the top bar already carries the sale's identity, and a full red pill up
// there reads as an alarm about the whole console rather than about the video
// feed. The dot carries the state, and pulses while healthy.
defineOptions({ name: 'ConnectionPill' });

const props = defineProps({
  // healthy | degraded | offline
  state: {
    type: String,
    default: 'healthy',
    validator: (value) => ['healthy', 'degraded', 'offline'].includes(value),
  },
});

const LABELS = { healthy: 'Stream healthy', degraded: 'Stream degraded', offline: 'Offline' };
const label = computed(() => LABELS[props.state] ?? LABELS.healthy);
</script>

<style scoped>
.connection-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: var(--p-connection-pill-dot-border-radius);
  display: inline-block;
}

/* The halo is the dot's own color at 20%, so a tone change moves both. */
.connection-pill__dot--healthy {
  background: var(--p-connection-pill-dot-healthy-background);
  box-shadow: var(--p-connection-pill-dot-healthy-box-shadow);
  animation: connection-pill-pulse 1.3s infinite;
}

.connection-pill__dot--degraded {
  background: var(--p-connection-pill-dot-degraded-background);
  box-shadow: var(--p-connection-pill-dot-degraded-box-shadow);
}

.connection-pill__dot--offline {
  background: var(--p-connection-pill-dot-offline-background);
  box-shadow: var(--p-connection-pill-dot-offline-box-shadow);
}

/* The console's shared "live" pulse. It was one global keyframe in the app's
   stylesheet, which a component in a library cannot count on; each component
   that pulses now carries its own copy under its own name. */
@keyframes connection-pill-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.85);
    opacity: 0.65;
  }
}

</style>
